import { onFCP, onTTFB } from 'web-vitals'
import {
    onCLS,
    onINP,
    onLCP,
    CLSMetricWithAttribution,
    INPMetricWithAttribution,
    LCPMetricWithAttribution,
    MetricWithAttribution,
} from 'web-vitals/attribution'
import { useBasketStore } from '~/stores/basket'
import { useAbTestStore } from '~/stores/abTest'
import { useLayoutStore } from '~/stores/layout'
import { useAuthStore } from '~/stores/auth'
import getDevice from '~/extensions/getDevice'
import { usePerformanceStore } from '~/stores/performace'

function initAbTests() {
    const layoutStore = useLayoutStore()
    const abTestStore = useAbTestStore()
    abTestStore.setAbTestNames(layoutStore.abTests)
}

function initZendeskButton() {
    setTimeout(() => {
        const zendeskButton = document.getElementById('zdbutton')

        zendeskButton?.addEventListener('click', () => {
            zendeskButton.classList.add('d-none')
        })
    }, 2000)
}

function initUidCookie() {
    const basketStore = useBasketStore()
    const uidCookie = useUidCookie()
    if (basketStore.uid) {
        uidCookie.value = basketStore.uid
    }
}

// The traffic-source middleware can only stash a visit (no uid exists yet for a fresh anonymous visitor - see
// trafficSourceTracking.global.ts). Once a uid does show up, anywhere in the app (basket creation, login), send it
// then, so it still reconciles to the customer at login/checkout the same way an already-sent visit does.
function initVisitTrackingFlush() {
    const uidCookie = useUidCookie()
    const pendingVisitTracking = usePendingVisitTrackingCookie()

    const flush = () => {
        const pending = pendingVisitTracking.value
        if (!pending || !uidCookie.value) {
            return
        }

        pendingVisitTracking.value = null

        useNuxtApp()
            .$repository.trackVisit({ uid: uidCookie.value, ...pending }, useAccessTokenCookie().value || '')
            .catch(() => {})
    }

    flush()
    watch(uidCookie, flush)
}

function initBasket() {
    const nuxtApp = useNuxtApp()
    const basketStore = useBasketStore()
    let initialized = false
    nuxtApp.hook('page:finish', () => {
        if (initialized) return
        initialized = true
        requestAnimationFrame(() => {
            if (!basketStore.isInitialized) {
                basketStore.fetchData(true)
            }
        })
    })
}

async function initAuth() {
    const authStore = useAuthStore()
    await authStore.initFromCookies()
}

// @see https://github.com/GoogleChrome/web-vitals/blob/main/src/lib/getSelector.ts
function getName(node: Node) {
    const name = node.nodeName
    return node.nodeType === 1 ? name.toLowerCase() : name.toUpperCase().replace(/^#/, '')
}

function getSelector(node: Node | null | undefined, maxLen?: number) {
    let sel = ''

    try {
        while (node && node.nodeType !== 9) {
            const el: Element = node as Element
            const part = el.id
                ? '#' + el.id
                : getName(el) +
                  (el.classList && el.classList.value && el.classList.value.trim() && el.classList.value.trim().length
                      ? '.' + el.classList.value.trim().replace(/\s+/g, '.')
                      : '')
            if (sel.length + part.length > (maxLen || 100) - 1) return sel || part
            sel = sel ? part + '>' + sel : part
            if (el.id) break
            node = el.parentNode
        }
    } catch (err) {
        // Do nothing...
    }

    return sel
}

const INTERACTION_EVENTS = ['pointerdown', 'pointerup', 'click', 'keydown', 'keyup']
const MAX_REMEMBERED_TARGETS = 50

// https://cdn.example.com/script.js?v=1 -> https://cdn.example.com/script.js (query strings break aggregation)
// Google tag scripts keep their id: gtm.js?id=GTM-… and gtag/js?id=G-…/AW-… differ only by it.
function getUrlWithoutQuery(url: string) {
    try {
        const parsed = new URL(url)
        const id = parsed.hostname === 'www.googletagmanager.com' ? parsed.searchParams.get('id') : null

        return parsed.origin + parsed.pathname + (id ? `?id=${id}` : '')
    } catch (err) {
        return url
    }
}

function rememberTarget(targets: Map<number, string>, timeStamp: number, target: string) {
    targets.set(timeStamp, target)
    if (targets.size > MAX_REMEMBERED_TARGETS) {
        targets.delete(targets.keys().next().value as number)
    }
}

// Event timing entry startTime is the event timeStamp, only rounded differently in some browsers.
function findTarget(targets: Map<number, string>, timeStamp: number) {
    for (const [time, target] of targets) {
        if (Math.abs(time - timeStamp) < 1) return target
    }

    return null
}

/**
 * web-vitals reads the INP target from the event timing entry after the next paint. When the handler removes
 * the element (modal close, v-if, list re-render), the entry target is null and the INP has no element.
 * So selectors are remembered when the event fires and kept for the interactions slow enough to become INP.
 */
function initInteractionTargets() {
    const recentTargets = new Map<number, string>()
    const slowTargets = new Map<number, string>()

    INTERACTION_EVENTS.forEach((type) => {
        document.addEventListener(
            type,
            (e) => {
                rememberTarget(recentTargets, e.timeStamp, getSelector(e.target as Node))
            },
            { capture: true, passive: true }
        )
    })

    if (PerformanceObserver.supportedEntryTypes?.includes('event')) {
        new PerformanceObserver((list) => {
            list.getEntries().forEach((entry) => {
                const target = recentTargets.get(entry.startTime) ?? findTarget(recentTargets, entry.startTime)
                if (target) {
                    rememberTarget(slowTargets, entry.startTime, target)
                }
            })
        }).observe({ type: 'event', durationThreshold: 40 } as PerformanceObserverInit)
    }

    return (timeStamp: number) => findTarget(slowTargets, timeStamp) ?? findTarget(recentTargets, timeStamp)
}

// Raw performance entries can exceed the sendBeacon limit, so only the summary needed in ELK is sent.
function summarizeInp(metric: INPMetricWithAttribution, findInteractionTarget: (timeStamp: number) => string | null) {
    const attribution = metric.attribution
    const script = attribution.longestScript?.entry
    const rememberedTarget = attribution.interactionTarget ? null : findInteractionTarget(attribution.interactionTime)

    let interactionTargetSource = 'unknown'
    if (attribution.interactionTarget) {
        interactionTargetSource = 'web-vitals'
    } else if (rememberedTarget) {
        interactionTargetSource = 'event-listener'
    }

    return {
        ...metric,
        entries: [],
        attribution: {
            interactionTarget: attribution.interactionTarget || rememberedTarget || '',
            interactionTargetSource,
            interactionType: attribution.interactionType,
            interactionTime: attribution.interactionTime,
            inputDelay: attribution.inputDelay,
            processingDuration: attribution.processingDuration,
            presentationDelay: attribution.presentationDelay,
            loadState: attribution.loadState,
            totalScriptDuration: attribution.totalScriptDuration,
            totalStyleAndLayoutDuration: attribution.totalStyleAndLayoutDuration,
            totalPaintDuration: attribution.totalPaintDuration,
            totalUnattributedDuration: attribution.totalUnattributedDuration,
            longestScript: script
                ? {
                      url: getUrlWithoutQuery(script.sourceURL),
                      functionName: script.sourceFunctionName,
                      invoker: script.invoker,
                      invokerType: script.invokerType,
                      subpart: attribution.longestScript?.subpart,
                      duration: script.duration,
                      intersectingDuration: attribution.longestScript?.intersectingDuration,
                      forcedStyleAndLayoutDuration: script.forcedStyleAndLayoutDuration,
                      // a script without a URL is inline (char position in the page) or runs in another frame
                      // (an iframe's own script), which windowAttribution tells apart
                      sourceCharPosition: script.sourceCharPosition,
                      windowAttribution: script.windowAttribution,
                  }
                : null,
        },
    }
}

function summarizeLcp(metric: LCPMetricWithAttribution) {
    const attribution = metric.attribution

    return {
        ...metric,
        entries: [],
        attribution: {
            target: attribution.target ?? '',
            // data: URIs of inline images would bloat the document
            url: attribution.url?.startsWith('data:') ? 'data:' : attribution.url ?? '',
            timeToFirstByte: attribution.timeToFirstByte,
            resourceLoadDelay: attribution.resourceLoadDelay,
            resourceLoadDuration: attribution.resourceLoadDuration,
            elementRenderDelay: attribution.elementRenderDelay,
        },
    }
}

function summarizeCls(metric: CLSMetricWithAttribution) {
    const attribution = metric.attribution

    return {
        ...metric,
        entries: [],
        attribution: {
            largestShiftTarget: attribution.largestShiftTarget ?? '',
            largestShiftTime: attribution.largestShiftTime,
            largestShiftValue: attribution.largestShiftValue,
            loadState: attribution.loadState,
        },
    }
}

function createPageViewId() {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function initCoreWebVitals() {
    let lastTTFB: number | null = null
    let lastFCP: number | null = null
    let lcpData: LCPMetricWithAttribution | null = null

    const findInteractionTarget = initInteractionTargets()
    const performanceStore = usePerformanceStore()
    // Links LCP, CLS and INP of one page view in ELK
    const pageViewId = createPageViewId()
    // INP and CLS are reported when the page is hidden, possibly after client-side navigations
    const landingPath = useRoute().fullPath

    const cwvCallback = (metric: MetricWithAttribution) => {
        const route = useRoute()
        const config = useRuntimeConfig()
        const nuxtApp = useNuxtApp()
        let timings = {} as { [name: string]: number | null }
        let inputData: object = metric
        if (metric.name === 'LCP') {
            // Copy, otherwise the timings below are written into the store state
            timings = { ...performanceStore.list }
            timings.ttfb = lastTTFB
            timings.fcp = lastFCP
            timings.lcp = metric.value

            inputData = summarizeLcp(metric)
        }
        if (metric.name === 'INP') {
            inputData = summarizeInp(metric, findInteractionTarget)
        }
        if (metric.name === 'CLS') {
            inputData = summarizeCls(metric)
        }
        inputData = {
            path: route.fullPath,
            landingPath,
            pageViewId,
            urlType: route.matched[0]?.meta.routeType,
            isServerRender: process.client && nuxtApp.isHydrating && nuxtApp.payload.serverRendered,
            lastTTFB,
            device: getDevice(),
            isPwa: true,
            timings: JSON.parse(JSON.stringify(timings)),
            ...inputData,
        }

        const url = `${config.public.apiUrl}/api/v2/log/web-vitals`
        // sendBeacon returns false when the payload is over the limit or the queue is full
        if (navigator.sendBeacon && navigator.sendBeacon(url, JSON.stringify(inputData))) {
            return
        }

        $fetch(url, {
            method: 'POST',
            body: inputData,
        })
    }

    onTTFB((inputData) => {
        lastTTFB = inputData.value
        if (lcpData !== null && lastFCP !== null) {
            cwvCallback(lcpData)
        }
    })

    onFCP((inputData) => {
        lastFCP = inputData.value
        if (lcpData !== null && lastTTFB !== null) {
            cwvCallback(lcpData)
        }
    })

    onLCP((inputData) => {
        lcpData = inputData
        if (lastTTFB !== null && lastFCP !== null) {
            cwvCallback(inputData)
        }
    })

    onINP(
        (inputData) => {
            cwvCallback(inputData)
        }
        // { reportAllChanges: true }
    )
    onCLS(
        (inputData) => {
            cwvCallback(inputData)
        }
        // { reportAllChanges: true }
    )
}

export default defineNuxtPlugin(async () => {
    await initAuth()
    await initBasket()
    initUidCookie()
    initVisitTrackingFlush()
    initAbTests()
    initZendeskButton()
    initCoreWebVitals()
})
